import { useTranslation } from "react-i18next";
import { Box, Flex } from "styled-system/jsx";
import {
  ControlledInput,
  ControlledInputProps,
  ControlledInputWithIcon,
  ControlledSelectRoot,
  ControlledSelectRootProps,
} from "shared/components";
import {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
  useWatch,
} from "react-hook-form";
import { Button, Select, Text } from "@ttc3k/trekker";
import { Lock, LockSlash, Undo } from "iconoir-react";
import { useEffect, useState } from "react";
import {
  ExistingUserEmailStatus,
  useInviteStaffMember,
} from "./useInviteStaffMember";
import { css } from "styled-system/css";
import {
  ContactRole,
  EnumUserAccessOperatorsRole,
  EnumUserStatus,
  UserWithPictureTc,
} from "gql/generated";
import { defaultHiddenEmailStart } from "shared/utils/defaultHiddenEmailStart";

export interface IInviteStaffMember {
  photo: string;
  email: string;
  firstName: string;
  lastName: string;
  title?: string;
  role: string;
  contactRoles?: ContactRole[];
  sendInvitationEmail: boolean;
}

type InviteStaffMemberProps<T extends FieldValues> = {
  user?: Partial<UserWithPictureTc>;
  entityId: string;
  isForOrganization: boolean;
  form: UseFormReturn<T>;
  inputNameEmailProps: ControlledInputProps;
  inputNameFirstNameProps: ControlledInputProps;
  inputNameLastNameProps: ControlledInputProps;
  inputNameRoleProps: Omit<ControlledSelectRootProps, "collection">;
  onExistingUserEmailStatusChange: (status: ExistingUserEmailStatus) => void;
  onValidatingEmailChange?: (isValidating: boolean) => void;
  viewOnly?: boolean;
  hideRoleField?: boolean;
};

export const InviteStaffMember = <T extends FieldValues>({
  user,
  entityId,
  isForOrganization,
  form,
  inputNameEmailProps,
  inputNameFirstNameProps,
  inputNameLastNameProps,
  inputNameRoleProps,
  onExistingUserEmailStatusChange,
  onValidatingEmailChange,
  viewOnly = false,
  hideRoleField = false,
}: InviteStaffMemberProps<T>) => {
  const { t } = useTranslation("core", {
    keyPrefix: "STAFF.ADD_EDIT_PERSON",
  });
  const { existingUserEmailStatus, namesDisabled, isValidatingEmail, handleValidateEmail } =
    useInviteStaffMember<T>({
      user,
      entityId,
      form,
      inputNameEmailProps,
      inputNameFirstNameProps,
      inputNameLastNameProps,
      onExistingUserEmailStatusChange,
    });

  // Notify parent when validation state changes
  useEffect(() => {
    onValidatingEmailChange?.(isValidatingEmail);
  }, [isValidatingEmail, onValidatingEmailChange]);

  const [isEmailUnlocked, setIsEmailUnlocked] = useState(false);

  const email = useWatch({
    control: form.control,
    name: inputNameEmailProps.name as Path<T>,
  });
  const role = useWatch({
    control: form.control,
    name: inputNameRoleProps.name as Path<T>,
  });
  // Handle resetting the email to an empty string if the user currently has a hidden email address
  // and the new role requires an email address
  useEffect(() => {
    // If the user currently has a hidden email address,
    // we need to check to see if the new role requires an email address
    if (user?.email?.startsWith(defaultHiddenEmailStart)) {
      if (role !== EnumUserAccessOperatorsRole.NoAccess) {
        // Reset the email to an empty string
        form.setValue("email" as Path<T>, "" as PathValue<T, Path<T>>, {
          shouldDirty: true,
        });
      } else {
        // Reset the email to the current email
        form.setValue(
          "email" as Path<T>,
          user?.email as PathValue<T, Path<T>>,
          {
            shouldDirty: true,
          },
        );
      }
    }

    form.trigger("email" as Path<T>);
  }, [form, role, user]);

  const roleCollection = Select.createCollection({
    items: [
      {
        label: t("LABELS.ROLE_OWNER"),
        value: EnumUserAccessOperatorsRole.Owner,
      },
      {
        label: t("LABELS.ROLE_EDITOR"),
        value: EnumUserAccessOperatorsRole.Editor,
      },
      // DEVELOPER is an org-only role used by the Developer Zone to view
      // org-scoped API keys and GraphQL examples. Not exposed for operators.
      ...(isForOrganization
        ? [
            {
              label: t("LABELS.ROLE_DEVELOPER"),
              value: EnumUserAccessOperatorsRole.Developer,
            },
          ]
        : [
            {
              label: t("LABELS.ROLE_VIEW_ONLY"),
              value: EnumUserAccessOperatorsRole.ViewOnly,
            },
            {
              label: t("LABELS.ROLE_NO_ACCESS"),
              value: EnumUserAccessOperatorsRole.NoAccess,
            },
          ]),
    ],
  });

  return (
    <Flex flexDir="column" gap="150" width="full" position="relative">
      {!email?.startsWith(defaultHiddenEmailStart) ? (
        <Flex direction="column" gap="50" w="full">
          <Flex alignItems="flex-end" gap="150" w="full">
            <Box flex="1">
              <ControlledInputWithIcon
                IconElement={Undo}
                iconClassName={css({
                  color: "icon.mid",
                  transform: "scaleY(-1)",
                })}
                label={t("LABELS.EMAIL")}
                placeholder={
                  role === EnumUserAccessOperatorsRole.NoAccess
                    ? t("LABELS.OPTIONAL")
                    : t("LABELS.REQUIRED")
                }
                inputMode="email"
                {...inputNameEmailProps}
                onBlurCapture={handleValidateEmail}
                type="email"
                onPressEnterToSearch={handleValidateEmail}
                disabled={
                  (user?.status === EnumUserStatus.Enrolled &&
                    !user?.email?.startsWith(defaultHiddenEmailStart)) ||
                  (user?.status === EnumUserStatus.Provisioned &&
                    !isEmailUnlocked) ||
                  viewOnly
                }
                autoFocus={false}
              />
            </Box>
            {user?.status === EnumUserStatus.Provisioned && !viewOnly && (
              <Button
                mode="light"
                visual="unstyled"
                type="button"
                onClick={() => setIsEmailUnlocked(!isEmailUnlocked)}
                h="40px"
                px="100"
                border="1px solid"
                borderColor="border.light"
                borderRadius="100"
                alignSelf="flex-end"
                style={{ transform: "translateY(-3px)" }}
                _hover={{
                  bgColor: "bg.wa.lightest",
                }}
              >
                {isEmailUnlocked ? (
                  <LockSlash width="18px" />
                ) : (
                  <Lock width="18px" />
                )}
              </Button>
            )}
          </Flex>
          {isValidatingEmail ? (
            <Text visual="smallRegular" color="text.mid">
              Searching database for existing user...
            </Text>
          ) : (
            (user?.status === EnumUserStatus.Enrolled ||
              user?.status === EnumUserStatus.Provisioned) && (
              <Text visual="smallRegular" color="text.light">
                {user?.status === EnumUserStatus.Enrolled
                  ? t("LABELS.PRIMARY_EMAIL_HINT")
                  : t("LABELS.PRIMARY_EMAIL_HINT_INVITED")}
              </Text>
            )
          )}
        </Flex>
      ) : (
        <Flex flexDir="column" gap="0">
          <Text visual="smallMedium" color="text.mid">
            {t("LABELS.EMAIL")}
          </Text>
          <Button
            mode="light"
            my="50"
            h="40px"
            onClick={() => {
              form.setValue("email" as Path<T>, "" as PathValue<T, Path<T>>, {
                shouldDirty: true,
              });
            }}
          >
            {t("CTA_BUTTON.SET_EMAIL")}
          </Button>
        </Flex>
      )}

      {existingUserEmailStatus !== ExistingUserEmailStatus.None && (
        <Text
          color={
            existingUserEmailStatus === ExistingUserEmailStatus.Duplicate ||
            existingUserEmailStatus === ExistingUserEmailStatus.AlreadyAttached
              ? "text.error.dark"
              : "text.accent.purple.mid"
          }
          visual="smallRegular"
          marginTop="-12px"
        >
          {t(`MESSAGES.USER_EXISTS_${existingUserEmailStatus}`)}
        </Text>
      )}

      <Flex
        flexDir={{
          base: "column",
          md: "row",
        }}
        justifyContent="space-between"
        alignItems="flex-start"
        gap="150"
      >
        <ControlledInput
          label={t("LABELS.FIRST_NAME")}
          placeholder={t("LABELS.REQUIRED")}
          flex="1"
          inputMode="text"
          {...inputNameFirstNameProps}
          disabled={namesDisabled || !!user?._id || viewOnly}
        />
        <ControlledInput
          label={t("LABELS.LAST_NAME")}
          placeholder={t("LABELS.REQUIRED")}
          flex="1"
          inputMode="text"
          {...inputNameLastNameProps}
          disabled={namesDisabled || !!user?._id || viewOnly}
        />
      </Flex>

      {!hideRoleField && (
        <>
          {/* ROLE */}
          <ControlledSelectRoot
            name={inputNameRoleProps.name as Path<T>}
            label={t("LABELS.ROLE")}
            placeholder={t("LABELS.ROLE_PLACEHOLDER")}
            flex="1"
            collection={roleCollection}
            value={[role]}
            multiple={false}
            onValueChange={({ value }: { value: string[] }) => {
              form.setValue(
                inputNameRoleProps.name as Path<T>,
                value[0] as PathValue<T, Path<T>>,
                {
                  shouldDirty: true,
                },
              );
            }}
            disabled={viewOnly}
          >
            {roleCollection.items.map(item => (
              <Select.Item key={item.value} item={item}>
                {item.label}
              </Select.Item>
            ))}
          </ControlledSelectRoot>
          <Text visual="smallRegular" color="text.light">
            {t(`LABELS.ROLE_HINT_${role}`)}
          </Text>
        </>
      )}
    </Flex>
  );
};
